import React from 'react';
import axios from 'axios';

import styles from './SchemaBase.module.scss';
import { useTranslate } from 'react-polyglot';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Modal from '../../Shared/Modal/Modal';
import ReactMarkdown from 'react-markdown';

function SchemaBase(props: SchemaBaseProps) {
  const t = useTranslate();
  const navigate = useNavigate();

  const [sending, setSending] = React.useState(false);
  const [sentSuccess, setSentSuccess] = React.useState(false);
  const [showGdprModal, setShowGdprModal] = React.useState(false);
  const [gdprAccepted, setGdprAccepted] = React.useState(false);

  // Check if GDPR text exists and is not empty
  const hasGdprText = props.schema?.GDPR && props.schema.GDPR.trim() !== "";

  function sendApplication() {
    setSending(true);

    // Custom replacer to handle circular references and React elements
    const getCircularReplacer = () => {
      const seen = new WeakSet();
      return (key: string, value: any) => {
        // Skip React elements and other non-serializable objects
        if (value !== null && typeof value === "object") {
          if (seen.has(value)) {
            return;
          }
          seen.add(value);
          // Skip React elements (have $$typeof property)
          if (value.$$typeof) {
            return;
          }
        }
        return value;
      };
    };

    // Clean clone removing circular references
    const cleanSchema = JSON.parse(JSON.stringify(props.schema, getCircularReplacer()));

    if (props.schemaKey != "dynschema") {
      return new Promise((resolve, reject) => {
        axios.put(
          "/api/schema",
          {
            schema: cleanSchema,
            schemaKey: props.schemaKey,
            breedId: props.breedId
          },
          {
            responseType: 'json',
          }
        ).then((res: any) => {
          resolve(res);
          if (props.onSchemaSent) {
            props.onSchemaSent();
          }
        }).catch((err: any) => {
          reject(err);
        }).finally(() => {
          setSending(false);
        });
      });
    } else { // SEND DYNAMIC SCHEMA
      return new Promise((resolve, reject) => {
        axios.put(
          "/api/dynschema/" + props.breedId + "/" + cleanSchema.ID,
          cleanSchema,
          {
            responseType: 'json',
          }
        ).then((res: any) => {
          resolve(res);
        }).catch((err: any) => {
          console.log(err);
          reject(err);
        }).finally(() => {
          setSending(false);
        });
      });
    }
  }
  return (
    <div className={styles.schemabase}>
      <div className={styles.header}>
        <h1>{props.title}</h1>
        <p>{props.description}</p>
          {/* GDPR Link and checkbox - Only show when there's GDPR text available */}
        {hasGdprText && (
          <div className={styles.gdprSection}>
            <div className={styles.gdprLink}>
              <a 
                href="#" 
                onClick={(e) => {
                  e.preventDefault();
                  setShowGdprModal(true);
                }}
              >
                {t("schema.base.gdpr.button") || "View Privacy Information"}
              </a>
            </div>
            <div className={styles.gdprCheckbox}>
              <label>
                <input
                  type="checkbox"
                  checked={gdprAccepted}
                  onChange={(e) => setGdprAccepted(e.target.checked)}
                />
                <span>{t("schema.base.gdpr.checkbox") || " I have read and accept the privacy policy"}</span>
              </label>
            </div>
          </div>
        )}
      </div>
      <div className={styles.content}>
        {!sentSuccess ?
          props.children
        :
          <div
            className={styles.success}
          >
            <hr/>
            <h2>{t("schema.base.content.success.title")}</h2>
            <p>{t("schema.base.content.success.text")}</p>
          </div>
        }
      </div>
      {props.disableSubmit ? null :
        <div className={styles.footer}>
          {!sentSuccess ?
            <button 
              className="btn btn-primary" 
              disabled={hasGdprText && !gdprAccepted}
              onClick={() => {
              sendApplication().then((res: any) => {
                setSentSuccess(true);
              }).catch((err: any) => {
                toast.error(t("schema.base.footer.error"));
              });
            }}>
              {sending ?
                t("schema.base.footer.sending")
              :
                t("schema.base.footer.submit")
              }
            </button>
          : null}
          {hasGdprText && !gdprAccepted && !sentSuccess ? (
            <div className={styles.gdprWarning}>
              {t("schema.base.gdpr.warning") || "You must accept the privacy policy to submit the form"}
            </div>
          ) : null}
        </div>
      }      {/* GDPR Modal */}
      <Modal
        title={t("schema.base.gdpr.modal.title") || "Privacy & Data Protection"}
        show={showGdprModal}
        onClose={() => setShowGdprModal(false)}
      >
        <div>
          <ReactMarkdown>
            {props.schema?.GDPR}
          </ReactMarkdown>
        </div>
      </Modal>
    </div>
  );
}

class SchemaBaseProps {
  title?: string;
  description?: string;
  schema?: any;
  schemaKey?: string;
  breedId?: string;
  disableSubmit?: boolean = false;
  onSchemaSent?: () => void;

  children?: React.ReactNode;
}

export default SchemaBase;